import { Lightning } from '@lightningjs/sdk';
import Store from 'lightningjs-redux';
import merge from 'lodash/merge';
import { getInitialStyle } from './privacySettings.style';
import { getBackButtonLabel, isUpdated } from './privacySettings.utils';
import connect from './privacySettings.connector';
import { CheckBoxBlock } from '../../../components/checkBoxBlock/checkBoxBlock.component';
import { Button } from '../../tornado/button/button.component';
import Icon from '../../tornado/icon/icon.component';
import { BrandIcon } from '../../tornado/brand-icon/brandIcon.component';
import { t } from '../../i18n';
import { setUserConsents } from '../../../logic/user/user.actions';
import { sendEvent } from '../../../logic/analytics/analytics.utils';
import { PRIVACY_SETTINGS_EXIT_SIGNAL } from './privacySettings.constants';

export class PrivacySettings extends Lightning.Component {
  static _template() {
    return merge(
      {
        Background: {},
        BackButton: {
          type: Button,
          label: t('global.back'),
        },
        ArrowUp: {
          type: Icon,
        },
        BrandIcon: {
          type: BrandIcon,
        },
        ScrollWrapper: {
          ScrollContent: {
            Title: {
              text: {
                text: t('privacySettings.title'),
              },
            },
            Description: {
              visible: t('privacySettings.cookie.text'),
              text: {
                text: t('privacySettings.cookie.text'),
              },
            },
            CookieGroups: {},
          },
        },
        ArrowDown: {
          type: Icon,
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._index = 0;
    this._focusable = [];
    this._visibility = [];
    this._positions = [0];
    this._savedCookies = {};
  }

  _attach() {
    this._unsubscribe = connect(this);
  }

  _detach() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
  }

  _getFocused() {
    // FIXME: hacky way to get actual finalH of CookieGroups
    this.setPositions();

    return this.focused;
  }

  get focused() {
    return this._focusable[this._index];
  }

  setPositions() {
    // do nothing if positions are already set
    if (this._positions.length > 1) {
      return;
    }

    this.stage.update();
    this._positions = [0];
    let offset = this.tag('CookieGroups').finalY;
    this._visibility = [
      {
        BrandIcon: true,
        ArrowUp: false,
        ArrowDown: this.tag('ScrollContent').finalH > this.tag('ScrollWrapper').finalH,
      },
    ];
    const bottomOffset = this.tag('ScrollWrapper').finalH - this.tag('ScrollContent').finalH;
    const halfOfScreenHeight = this.tag('ScrollWrapper').finalH / 2;

    this.tag('CookieGroups').children.forEach(block => {
      const position = Math.min(Math.max(halfOfScreenHeight - offset - Math.round(block.finalH / 2), bottomOffset), 0);
      this._positions.push(position);
      offset += block.finalH;
      const isBrandIconVisible = position === 0;
      this._visibility.push({
        BrandIcon: isBrandIconVisible,
        ArrowUp: !isBrandIconVisible,
        ArrowDown: position !== bottomOffset,
      });
    });

    // update visibility for items
    this.setIndex();
  }

  setIndex(index = this._index) {
    this._index = index;
    const itemsVisibility = this._visibility[index];
    this.patch({
      ArrowUp: {
        visible: itemsVisibility.ArrowUp,
      },
      BrandIcon: {
        visible: itemsVisibility.BrandIcon,
      },
      ScrollWrapper: {
        ScrollContent: {
          smooth: {
            y: index ? this._positions[index] : 0,
          },
        },
      },
      ArrowDown: {
        visible: itemsVisibility.ArrowDown,
      },
    });
  }

  get selectedCookies() {
    return this.tag('CookieGroups').children.reduce(
      (memo, item) => ({
        ...memo,
        [item.category]: item.checked,
      }),
      {},
    );
  }

  _dispatchSelectedCategories() {
    const shouldSendEvent = !this._areGdprCookiesSet || isUpdated(this.savedCookies, this.selectedCookies);
    const consent = this.selectedCookies;
    Store.dispatch(setUserConsents(consent));

    if (shouldSendEvent) {
      sendEvent({
        key: 'gdprChangeConsents',
        consentNames: Object.keys(consent)
          .filter(key => consent[key])
          .join(','),
      });
    }
  }

  _handleUp() {
    if (this._index > 0) {
      this.setIndex(this._index - 1);

      return true;
    }

    return false;
  }

  _handleDown() {
    if (this._index < this._focusable.length - 1) {
      this.setIndex(this._index + 1);

      return true;
    }

    return false;
  }

  _handleEnter() {
    if (this.focused === this.tag('BackButton')) {
      this._dispatchSelectedCategories();
      this.signal(PRIVACY_SETTINGS_EXIT_SIGNAL);
    }
    this.tag('BackButton').label = getBackButtonLabel(this.savedCookies, this.selectedCookies, this._areGdprCookiesSet);

    return true;
  }

  _handleBack() {
    this._dispatchSelectedCategories();
    this.signal(PRIVACY_SETTINGS_EXIT_SIGNAL);

    return true;
  }

  _setCheckbox(category, value) {
    const checkbox = this.tag('CookieGroups').children.find(cookieCategory => cookieCategory.category === category);
    if (checkbox) {
      checkbox.checked = value;
    }
  }

  set cookies(value) {
    this._savedCookies = value;

    this.tag('CookieGroups').children = Object.keys(value).map(cookieCategory => ({
      type: CheckBoxBlock,
      data: {
        title: t(`privacySettings.cookie.${cookieCategory}.title`),
        description: t(`privacySettings.cookie.${cookieCategory}.content`),
      },
      category: cookieCategory,
    }));

    this._focusable = [this.tag('BackButton'), ...this.tag('CookieGroups').children];

    Object.entries(value).forEach(([category, value]) => {
      const checkbox = this.tag('CookieGroups').children.find(cookieCategory => cookieCategory.category === category);
      if (checkbox) {
        checkbox.checked = typeof value === 'boolean' ? value : false;
      }
    });
  }

  set areGdprCookiesSet(value) {
    this._areGdprCookiesSet = value;
  }

  get savedCookies() {
    return this._savedCookies;
  }
}
